import React from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import { injectIntl } from 'react-intl';
import Table from 'rc-table';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import dayjs from 'dayjs';
import { createStructuredSelector } from 'reselect';
import { selectInvoices } from '../selectors';
import { BaseComponent } from '../../../components/BaseComponent';
import { KanbanProgressBar } from '../../KanbanBoards/components/ProgressBar';
import { formatCurrency, formatLicensePlate } from '../../../utils/string';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDown, faArrowUp } from '@fortawesome/free-solid-svg-icons';
import { InvoiceListItem } from './InvoiceListItem';
import { InvoiceEmailStatus } from './InvoiceEmailStatus';
import { ViesLabel } from '../../Companies/components/ViesLabel';
import { getDate } from '../../Vehicles/helpers';
import { RegplateLabel } from '../../Vehicles/components/RegplateLabel';
import { isShowViesCheck } from '../../Companies/helpers';
import { VehicleAddToOrderCard } from '../../Vehicles/components/VehicleAddToOrderCard';
import { HBox } from '../../../components/Box';
import { INVOICE_DIRECTIONS, INVOICE_TYPES } from '../constants';
import { InvoiceCreateCopyModals } from '../pages/createIncomingCopy/AddIncomingInvoiceCopy';
import { Button } from '../../../components/Buttons';
import { CopyInvoiceModal } from './CopyInvoiceCard';

const Container = styled.div`
  width: 100%;
  background: white;
`;

const CustomTable = styled(Table)`
  width: 100%;
  display: flex;
  font-size: 12px;
  min-width: 1600px; /* Added minimum width */

  .rc-table-container {
    width: 100%;
  }

  .rc-table-content table {
    width: 100%;
  }

  .rc-table-thead {
    background-color: #eff2f7;
    font-weight: 600;
    height: 52px;
    padding: 16px 0;
    margin-left: 8px;
    font-size: 12px;
    color: #3c3d3e;
  }

  td,
  th {
    padding: 8px 12px;
  }
`;

const DescriptionCell = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 200px;
  justify-content: center;
`;

const PaidWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const IconWrapper = styled.div`
  color: ${props => (props?.color === 'green' ? '#11ab79' : props?.color === 'orange' ? '#FC9629' : '#d50046')};
`;

const ActionsContainer = styled.div`
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
`;

const VEHICLE_INCOMING_TYPE = 'vehicle incoming';
const PRODUCT_INVOICE_TYPE = 'invoice.subject.type.product';

/* eslint-disable react/prefer-stateless-function */
class InvoicesOverviewTableComp extends BaseComponent {
  constructor(props) {
    super(props);
  }

  get invoices() {
    return this.props.invoices;
  }

  isVehicleTranslatedTypeByDirection(item, direction, type) {
    const localSubject = this.lblStr(`invoice.subject.type.${type}`);
    const translatedType = `${localSubject} ${direction}`;
    return item?.type?.toLowerCase()?.includes(translatedType?.toLowerCase());
  }

  formatDate = dateString => {
    if (!dateString) return '-';
    return dayjs(dateString).format('DD-MM-YYYY');
  };

  getData = invoice => {
    const localSubject = this.lblStr(`invoice.subject.type.${invoice?.subjectType}`);
    const type = `${localSubject} ${invoice?.direction}`;
    const otherCompany = invoice?.direction === 'outgoing' ? invoice.outgoingCompany : invoice.incomingCompany;
    const companyName = otherCompany?.properties?.find(p => p?.name?.name === 'name')?.value ?? '-';
    const companySurname = otherCompany?.properties?.find(p => p?.name?.name === 'surname')?.value ?? '';
    const companyType = otherCompany?.properties?.find(p => p?.name?.name === 'company_type')?.value ?? '';
    const companyFrom = otherCompany?.properties?.find(p => p?.name?.name === 'company_from_eu')?.value ?? '';

    const isShowVies = isShowViesCheck(companyType, companyFrom);
    //TODO: add other company contact to invoice
    const contactName =
      invoice?.outgoingContact || invoice?.incomingContact
        ? invoice?.direction === 'outgoing'
          ? invoice?.outgoingContact
          : invoice?.incomingContact
        : null;

    const vehicle = invoice?.vehicle
      ? invoice?.vehicle?.properties?.reduce(
          (acc, nxt) => ({ ...acc, [nxt?.propertyName?.name]: nxt?.propertyValue?.value }),
          {},
        )
      : null;
    const statusColors = {
      PAID: 'green',
      PARTLY_PAID: 'orange',
      DECLINED: 'red',
      OPEN: 'red',
    };

    let paymentAmount;
    if (!invoice?.entryNumber && invoice?.amountFc === null) {
      paymentAmount = 'not_synced';
    } else {
      paymentAmount = invoice?.amountFc ? formatCurrency(invoice?.amountFc) : formatCurrency('0.00');
    }

    let date = this.formatDate(invoice?.date);

    if (invoice?.subjectType == 'product' && invoice?.type !== INVOICE_TYPES.CREDIT) {
      date = getDate(invoice?.createdAt);
    }

    return {
      type,
      credit: this.lblStr(`invoice.type.${invoice.type}`),
      copy: invoice,
      regplate: vehicle?.regplate,
      vin: `${vehicle?.vin ?? '-'}`,
      otherCompany: { company: `${companyName} ${companySurname}`, user: contactName ?? null },
      description: !vehicle
        ? { description: invoice?.description }
        : {
            name: `${vehicle?.brand_name} ${vehicle?.model_name}`,
            info: ` | ${vehicle?.model_info}`,
            vehicle: invoice?.vehicle,
          },
      invoiceNumber: {
        value: invoice?.entryNumber ?? '-',
        date: date,
        isIncoming: invoice?.direction === 'incoming',
      },
      viesCheckStatus: {
        status: invoice?.viesCheckStatus,
        date: invoice?.viesCheckDate,
        isShowVies,
      },
      total: {
        amount: invoice?.transactionAmountDc
          ? formatCurrency(invoice?.transactionAmountDc)
          : formatCurrency(invoice?.financialTotalAmount),
        isPaid: invoice?.status?.name === 'PAID' || invoice?.status?.name === 'PARTLY_PAID',
        color: statusColors[invoice?.status?.name],
        date: invoice?.paymentDate
          ? this.formatDate(invoice?.paymentDate)
          : this.lbl(`invoice.paid.${invoice?.status?.name}`),
        paymentAmount,
      },
      id: invoice?.id,
      actions: invoice,
      emailSent: invoice,
    };
  };

  getColumns = () => {
    return [
      { title: this.lblStr('invoice.column.type'), dataIndex: 'type', key: 'type', align: 'left' },
      {
        title: this.lblStr('invoice.column.regplate'),
        dataIndex: 'regplate',
        key: 'regplate',
        align: 'left',
        render(value) {
          if (!value) return '-';
          return <RegplateLabel regplate={value} width="80" />;
        },
      },
      { title: 'Chassis', dataIndex: 'vin', key: 'vin', align: 'left' },
      {
        title: this.lblStr('invoice.column.model'),
        dataIndex: 'description',
        key: 'description',
        align: 'left',
        render(value, row, index) {
          console.log('value?.vehicle', value.vehicle);
          return (
            <DescriptionCell>
              {value?.description ? (
                <span style={{ marginBottom: 5 }}>{value.description}</span>
              ) : (
                <span style={{ marginBottom: 5 }}>
                  <strong>{value?.name}</strong>
                  {value?.info}
                </span>
              )}
            </DescriptionCell>
          );
        },
      },
      {
        title: this.lblStr('invoice.column.relation'),
        dataIndex: 'otherCompany',
        key: 'otherCompany',
        align: 'left',
        render(value, row, index) {
          return (
            <DescriptionCell>
              <strong>{value?.company}</strong>
              {value?.user && <span>{value?.user}</span>}
            </DescriptionCell>
          );
        },
      },
      {
        dataIndex: 'viesCheckStatus',
        key: 'viesCheckStatus',
        align: 'left',
        render(value, date, index) {
          return (
            <DescriptionCell>
              {value.isShowVies && (
                <>
                  <ViesLabel vies={value?.status} />
                  {getDate(value?.date, true)}
                </>
              )}
            </DescriptionCell>
          );
        },
      },
      {
        title: this.lblStr('invoice.column.invoice'),
        dataIndex: 'invoiceNumber',
        key: 'invoiceNumber',
        align: 'left',
        render(value, row, index) {
          return (
            <DescriptionCell>
              <PaidWrapper>
                <IconWrapper color={value?.isIncoming && 'green'}>
                  <FontAwesomeIcon icon={value?.isIncoming ? faArrowDown : faArrowUp} />
                </IconWrapper>
                <strong>{value?.value}</strong>
              </PaidWrapper>
              <span>{value?.date}</span>
            </DescriptionCell>
          );
        },
      },

      {
        title: this.lblStr('invoice.column.amountAndRemaining'),
        dataIndex: 'total',
        key: 'total',
        align: 'left',
        render(value) {
          return (
            <PaidWrapper>
              <IconWrapper color={value?.color}></IconWrapper>
              <DescriptionCell>
                <strong>
                  {`${value?.amount}`} / {`${value?.paymentAmount}`}
                </strong>
              </DescriptionCell>
            </PaidWrapper>
          );
        },
      },
      {
        title: 'Credit',
        dataIndex: 'credit',
        key: 'credit',
        align: 'left',
      },
      {
        title: 'Copy',
        dataIndex: 'copy',
        key: 'copy',
        align: 'left',
        render(value) {
          if (value?.type?.toLowerCase() === 'credit' && value?.subjectType?.toLowerCase() !== 'bpm') {
            return (
              <HBox>
                <CopyInvoiceModal
                  copyFromInvoiceId={value?.originalInvoiceId}
                  vehicleId={value?.vehicleId}
                  direction={value?.direction}
                  subjectType={value?.subjectType}
                />
              </HBox>
            );
          }
          return <span>{value?.label}</span>;
        },
      },
      {
        title: 'Email',
        dataIndex: 'emailSent',
        key: 'emailSent',
        align: 'left',
        render(value) {
          return (
            <DescriptionCell>
              <InvoiceEmailStatus invoice={value} vehicleId={value?.vehicle?.id} />
            </DescriptionCell>
          );
        },
      },
      {
        key: 'actions',
        dataIndex: 'actions',
        render(value) {
          return (
            <ActionsContainer>
              <IconWrapper color={'green'}>
                <InvoiceListItem
                  invoice={value}
                  userCreatorId={value?.userCreatorId}
                  financialContactId={value?.financialContactId}
                  outgoingUserId={value?.outgoingUserId}
                  emailContactId={value?.emailContactId}
                  key={value?.id}
                />
              </IconWrapper>
              {/*<IconWrapper color={'green'} style={{ marginLeft: 15 }}>*/}
              {/*  <FontAwesomeIcon icon={faCheckCircle} />*/}
              {/*</IconWrapper>*/}
            </ActionsContainer>
          );
        },
      },
    ];
  };

  to(url, newWindow) {
    //
    if (newWindow) {
      window.open(url);
    } else {
      this.props.history.push(url);
    }
  }

  tofn(url) {
    return event => {
      this.to(url, event?.ctrlKey || event?.metaKey);
    };
  }

  render() {
    const columns = this.getColumns();
    const data = this.invoices?.map(invoice => {
      return this.getData(invoice);
    });
    this.formatDate(data[0]?.date);
    return (
      <Container>
        <CustomTable
          columns={columns}
          data={data}
          scroll={{ x: window.innerWidth < 975 ? 975 : false }}
          emptyText={this.lbl('vehicles.overview.column.no_invoice_found')}
          onRow={(record, index) => {
            return {
              onClick: event => {
                event.stopPropagation();

                if (this.isVehicleTranslatedTypeByDirection(record, 'incoming', 'vehicle')) {
                  return this.to(`/invoices/incoming/${record.id}`, event?.ctrlKey || event?.metaKey);
                }

                if (this.isVehicleTranslatedTypeByDirection(record, 'outgoing', 'vehicle')) {
                  return this.to(`/invoices/outgoing/${record.id}`, event?.ctrlKey || event?.metaKey);
                }
                if (this.isVehicleTranslatedTypeByDirection(record, 'outgoing', 'BPM')) {
                  return this.to(`/invoices/outgoing/${record.id}`, event?.ctrlKey || event?.metaKey);
                }

                if (this.isVehicleTranslatedTypeByDirection(record, 'outgoing', 'product')) {
                  let vehicleId = this.props.vehicleId || record?.actions?.vehicleId;
                  if (vehicleId) {
                    return this.to(
                      `/invoices/product-invoices/vehicle/${vehicleId}/invoice/${record.id}`,
                      event?.ctrlKey || event?.metaKey,
                    );
                  }

                  return this.to(`/invoices/product-invoices/invoice/${record?.id}`, event?.ctrlKey || event?.metaKey);
                }

                return this.to(`/invoices/${record.id}`, event?.ctrlKey || event?.metaKey);
              },

              invoice: this.props.invoices[index],
            };
          }}
        />
      </Container>
    );
  }
}

InvoicesOverviewTableComp.propTypes = {
  dispatch: PropTypes.func.isRequired,
  onItemClick: PropTypes.func,
};

const mapStateToProps = (state, ownProps) =>
  createStructuredSelector({
    invoices: selectInvoices(),
  });

function mapDispatchToProps(dispatch, ownProps) {
  return {
    dispatch,
  };
}

const withConnect = connect(mapStateToProps, mapDispatchToProps);

export const InvoicesOverviewTable = compose(withConnect, withRouter, injectIntl)(InvoicesOverviewTableComp);
